<template>
  <div>
    <v-row class="mx-0 mb-4">
      <v-spacer/>
      <v-btn class="mt-8 mt-md-0" @click="startNewRow" v-if="!newRowActive && editIndex === null">
        Agregar {{ catalog.singular || 'registro' }}
      </v-btn>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="catalog.items"
      item-value="name"
      class="elevation-0"
      hide-default-footer
    >
      <template #header="{ props }">
        <tr>
          <th v-for="header in props.headers" :key="header.value">
            {{ header.text }}
          </th>
          <th style="width:60px"></th>
        </tr>
      </template>

      <template v-slot:[`body.prepend`]>
        <tr v-if="newRowActive">
          <td v-for="field in catalog.fields" :key="field.value" style="vertical-align:middle">
            <v-text-field
              v-if="field.type !== 'color_picker'"
              v-model="newRow[field.value]"
              :label="field.label"
              density="compact"
              hide-details
              variant="solo"
            />
            <v-color-picker
              v-else
              v-model="newRow[field.value]"
              flat
              hide-canvas
              show-swatches
              swatches-max-height="100"
              swatches-per-row="8"
              :swatches="toSwatches(field.items)"
              hide-inputs
              width="220"
              style="margin: 0 auto"
            />
          </td>
          <td style="text-align:right;">
            <v-btn variant="text" size="small" @click="cancelNewRow">
              Cancelar
            </v-btn>
            <v-btn size="small" @click="saveNewRow">
              Guardar
            </v-btn>
          </td>
        </tr>
      </template>

      <template #item="{ item, index }">
        <tr>
          <td v-for="field in catalog.fields" :key="field.value" style="vertical-align:middle">
            <v-text-field
              v-if="editIndex === index && field.type !== 'color_picker'"
              v-model="editItem[field.value]"
              density="compact"
              hide-details
              variant="solo"
            />
            <v-color-picker
              v-else-if="editIndex === index && field.type === 'color_picker'"
              v-model="editItem[field.value]"
              flat
              hide-canvas
              show-swatches
              swatches-max-height="100"
              swatches-per-row="8"
              :swatches="toSwatches(field.items)"
              hide-inputs
              width="220"
              style="margin: 0 auto"
            />
            <template v-else>
              <template v-if="field.type === 'color_picker'">
                <span
                  v-if="item[field.value]"
                  :style="{ background: item[field.value], width: '22px', height: '22px', borderRadius: '5px', display: 'inline-block' }"
                />
              </template>
              <template v-else>
                {{ item[field.value] }}
              </template>
            </template>
          </td>
          <td style="text-align:right;">
            <v-btn
              v-if="editIndex !== index"
              icon
              variant="text"
              size="x-small"
              @click="startEditRow(index, item)"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>

            <template v-else>
              <v-btn variant="text" size="small" @click="cancelEdit">
                Cancelar
              </v-btn>
              <v-btn size="small" class="mr-2" @click="saveEditRow(index)">
                Guardar
              </v-btn>
            </template>

            <v-btn
              v-if="editIndex !== index"
              icon
              variant="text"
              size="x-small"
              @click="deleteRow(index)"
              class="ml-2"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, PropType } from 'vue';
import { useCatalogsStore } from '@/store/catalogs';
import { useBottomSheetStore } from '@/store/bottomSheet';

type TableHeader = {
  text: string;
  value: string;
  align?: 'start' | 'end' | 'center';
  sortable?: boolean;
};

type CatalogField = {
  value: string;
  label: string;
  type?: 'text' | 'color_picker' | string;
  items?: string[] | string[][];
};

type Catalog = {
  singular?: string;
  module?: string;
  fields: CatalogField[];
  items: Record<string, any>[];
};

export default defineComponent({
  name: 'EditableCatalog',
  props: {
    catalog: { type: Object as PropType<Catalog>, required: true },
  },
  emits: ['update'],
  setup(props, { emit }) {
    const catalogsStore = useCatalogsStore();
    const bottomSheetStore = useBottomSheetStore();

    const newRowActive = ref(false);
    const newRow = ref<Record<string, any>>({});
    const editIndex = ref<number | null>(null);
    const editItem = ref<Record<string, any>>({});

    const headers = computed<TableHeader[]>(() =>
  (props.catalog.fields || [])
    .map((field) => ({
      text: field.label,
      value: field.value,
      align: 'start' as any,
      sortable: true, // opcional si quieres habilitar sort por defecto
    }))
    // 👇 agrega align y sortable para que cumpla el tipo
    .concat([{ text: '', value: 'actions', align: 'end', sortable: false }])
);

    function toSwatches(items: any) {
      if (!items) return [];
      return Array.isArray(items[0]) ? items : [items];
    }

    function startNewRow() {
      newRowActive.value = true;
      newRow.value = {};
      props.catalog.fields.forEach((field) => {
        newRow.value[field.value] =
          field.type === 'color_picker' ? (Array.isArray(field.items) ? (field.items as any)[0] ?? '#F44336' : '#F44336') : '';
      });
      editIndex.value = null;
    }

    function cancelNewRow() {
      newRowActive.value = false;
      newRow.value = {};
    }

    async function saveNewRow() {
      for (const field of props.catalog.fields) {
        if (!newRow.value[field.value]) return;
      }
      const updated = [...props.catalog.items, { ...newRow.value }];
      await catalogsStore.updateCatalog({ ...props.catalog, items: updated });
      cancelNewRow();
      emit('update');
    }

    function startEditRow(index: number, item: any) {
      editIndex.value = index;
      editItem.value = { ...item };
      cancelNewRow();
    }

    function cancelEdit() {
      editIndex.value = null;
      editItem.value = {};
    }

    async function saveEditRow(index: number) {
      for (const field of props.catalog.fields) {
        if (!editItem.value[field.value]) return;
      }
      const updated = [...props.catalog.items];
      updated[index] = { ...editItem.value };
      await catalogsStore.updateCatalog({ ...props.catalog, items: updated });
      cancelEdit();
      emit('update');
    }

    function deleteRow(index: number) {
      const item = props.catalog.items[index];
      bottomSheetStore.openSheet({
        module: props.catalog.module || 'Catalogos',
        formTitle: `¿Seguro(a) que deseas eliminar "${item[props.catalog.fields[0].value]}"?`,
        props: {
          item,
          message: 'Esta acción no se puede deshacer.',
          action: async () => {
            const updated = props.catalog.items.filter((_: any, i: number) => i !== index);
            await catalogsStore.updateCatalog({ ...props.catalog, items: updated });
            emit('update');
          },
        },
      });
    }

    return {
      // props a template
      catalog: props.catalog,

      // state
      newRowActive,
      newRow,
      editIndex,
      editItem,
      headers,

      // methods
      toSwatches,
      startNewRow,
      cancelNewRow,
      saveNewRow,
      startEditRow,
      cancelEdit,
      saveEditRow,
      deleteRow,
    };
  },
});
</script>
